import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    API,
    createControleDossierLot,
    deleteControleDossierLotAttachment,
    getControleDossierLotAttachmentContent,
    getControleDossiersLots,
    updateControleDossierLot,
    uploadControleDossierLotAttachment,
} from "../../services/api";
import { useAuth } from "../../auth/AuthContext";

const DOSSIER_TYPE = "PRODUCTION_PDA";
const DOSSIER_TABS = [
    { key: "deconditionnement", label: "Déconditionnement" },
    { key: "production", label: "Production PDA" },
];

const inputStyle = {
    border: "1px solid #cbd5e1",
    borderRadius: "7px",
    padding: "8px 10px",
    fontSize: "13px",
    background: "white",
    color: "#0f172a",
    boxSizing: "border-box",
    width: "100%",
};

const buttonBase = {
    borderRadius: "7px",
    border: "1px solid #cbd5e1",
    padding: "8px 12px",
    fontSize: "13px",
    cursor: "pointer",
};

const thStyle = { padding: "10px", textAlign: "left", color: "#475569", fontSize: "12px", borderBottom: "1px solid #e2e8f0" };
const tdStyle = { padding: "10px", borderBottom: "1px solid #e2e8f0", color: "#0f172a", fontSize: "13px" };
const cardStyle = { background: "white", border: "1px solid #e2e8f0", borderRadius: "8px", padding: "14px", display: "grid", gap: "12px" };
const sectionStyle = { ...cardStyle, borderTop: "4px solid #2563eb" };
const labelStyle = { display: "grid", gap: "5px", color: "#334155", fontSize: "12px", fontWeight: 800 };
const headerCellStyle = { padding: "9px", border: "1px solid #cbd5e1", background: "#f1f5f9", color: "#0f172a", fontSize: "12px", fontWeight: 900 };
const bodyCellStyle = { padding: "8px", border: "1px solid #cbd5e1", verticalAlign: "top" };

function todayISO() {
    return new Date().toISOString().slice(0, 10);
}

function addDaysISO(date, days) {
    const parsed = date ? new Date(`${date}T00:00:00`) : new Date();
    parsed.setDate(parsed.getDate() + days);
    return parsed.toISOString().slice(0, 10);
}

function formatDate(date) {
    if (!date) return "-";
    const [year, month, day] = date.split("-");
    return `${day}/${month}/${year}`;
}

function defaultFormData(dossier = null) {
    return {
        meta: {
            dossierType: DOSSIER_TYPE,
            ehpad: dossier?.ehpadNom || "",
            dateEdition: dossier?.dateProduction || todayISO(),
            dateLotDebut: todayISO(),
            dateLotFin: addDaysISO(todayISO(), 7),
            numeroControlePharmacie: "",
        },
        productionRobot: {
            producteurRobot: "",
            residentsActifsPDose: "",
            residentsProduitsRobot: "",
            residentsLivresDDose: "",
        },
        deconditionnement: {
            campagneNumero: "",
            date: dossier?.dateProduction || todayISO(),
            lignePoste: "",
            pharmacienResponsable: "",
            lignes: [],
        },
        conditionsProduction: {
            heureDebut: "",
            heureFin: "",
            temperature: "",
            hygrometrie: "",
        },
        residents: [],
        incidents: { problemes: "", details: "" },
        controlesQualite: [],
        controlesQualiteMeta: { rouleauxCyclamed: "" },
        reparationControleFinal: {
            realisePar: "",
            controlePi: "",
            pontageRouleaux: "",
            dateHeure: "",
        },
        liberationLot: {
            pharmacien: "",
            date: "",
            heure: "",
            signatureCachet: "",
        },
        piecesJointes: [],
    };
}

function mergeFormData(data, dossier = null) {
    const base = defaultFormData(dossier);
    return {
        ...base,
        ...(data || {}),
        meta: { ...base.meta, ...(data?.meta || {}), dossierType: DOSSIER_TYPE },
        productionRobot: { ...base.productionRobot, ...(data?.productionRobot || {}) },
        deconditionnement: {
            ...base.deconditionnement,
            ...(data?.deconditionnement || {}),
            lignes: Array.isArray(data?.deconditionnement?.lignes) ? data.deconditionnement.lignes : [],
        },
        conditionsProduction: { ...base.conditionsProduction, ...(data?.conditionsProduction || {}) },
        residents: Array.isArray(data?.residents) && data.residents.length ? data.residents : base.residents,
        incidents: { ...base.incidents, ...(data?.incidents || {}) },
        controlesQualite: Array.isArray(data?.controlesQualite) && data.controlesQualite.length ? data.controlesQualite : base.controlesQualite,
        controlesQualiteMeta: { ...base.controlesQualiteMeta, ...(data?.controlesQualiteMeta || {}) },
        reparationControleFinal: { ...base.reparationControleFinal, ...(data?.reparationControleFinal || {}) },
        liberationLot: { ...base.liberationLot, ...(data?.liberationLot || {}) },
        piecesJointes: Array.isArray(data?.piecesJointes) ? data.piecesJointes : [],
    };
}

function statusLabel(status) {
    return status === "TERMINE" ? "Termine" : "Brouillon";
}

function apiErrorMessage(error, fallback) {
    const status = error?.response?.status;
    const serverMessage = error?.response?.data?.message || error?.response?.data?.error;
    if (status === 404) return `${fallback} Route API introuvable: redemarrez/rebuildez le backend.`;
    if (status === 403) return `${fallback} Droits insuffisants ou pharmacie superadmin non selectionnee.`;
    if (status === 401) return `${fallback} Session refusee par le backend.`;
    return serverMessage ? `${fallback} ${serverMessage}` : fallback;
}

export default function DossierLotProduction({ service = null }) {
    const { user } = useAuth() || {};
    const [ehpads, setEhpads] = useState([]);
    const [dossiers, setDossiers] = useState([]);
    const [selectedEhpadId, setSelectedEhpadId] = useState("");
    const [createEhpadId, setCreateEhpadId] = useState("");
    const [createDateProduction, setCreateDateProduction] = useState(todayISO());
    const [activeDossier, setActiveDossier] = useState(null);
    const [formData, setFormData] = useState(defaultFormData);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [saving, setSaving] = useState(false);
    const [attachmentUploading, setAttachmentUploading] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [activeTab, setActiveTab] = useState("deconditionnement");

    const selectedEhpad = useMemo(
        () => ehpads.find(e => String(e.id) === String(selectedEhpadId)),
        [ehpads, selectedEhpadId]
    );

    const filteredDossiers = useMemo(() => {
        const productionDossiers = dossiers.filter(dossier => dossier.donnees?.meta?.dossierType === DOSSIER_TYPE);
        return selectedEhpadId
            ? productionDossiers.filter(dossier => String(dossier.ehpadId) === String(selectedEhpadId))
            : productionDossiers;
    }, [dossiers, selectedEhpadId]);

    const loadDossiers = useCallback(() => {
        return getControleDossiersLots()
            .then(r => setDossiers(r.data || []))
            .catch(error => setError(apiErrorMessage(error, "Impossible de charger les dossiers de lot.")));
    }, []);

    useEffect(() => {
        Promise.all([
            API.get("/ehpads").then(r => setEhpads(r.data || [])),
            loadDossiers(),
        ]).finally(() => setLoading(false));
    }, [loadDossiers]);

    const updateSection = (section, patch) => {
        setFormData(data => ({ ...data, [section]: { ...(data[section] || {}), ...patch } }));
    };

    const updateRows = (section, rows) => {
        setFormData(data => ({ ...data, [section]: rows }));
    };

    const updateNestedRows = (section, rowsKey, rows) => {
        setFormData(data => ({
            ...data,
            [section]: { ...(data[section] || {}), [rowsKey]: rows },
        }));
    };

    const operatorLabel = user?.username || user?.email || "";

    const saveDossier = useCallback(async (status = activeDossier?.status || "BROUILLON", dataOverride = null) => {
        if (!activeDossier) return null;
        const donnees = dataOverride || formData;
        setSaving(true);
        setError("");
        try {
            const response = await updateControleDossierLot(activeDossier.id, { status, donnees });
            setActiveDossier(response.data);
            setFormData(mergeFormData(response.data.donnees, response.data));
            await loadDossiers();
            return response.data;
        } catch (error) {
            setError(apiErrorMessage(error, "Sauvegarde impossible."));
            return null;
        } finally {
            setSaving(false);
        }
    }, [activeDossier, formData, loadDossiers]);

    useEffect(() => {
        const handleShortcut = event => {
            if (!activeDossier || saving) return;
            if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "s") {
                event.preventDefault();
                saveDossier("BROUILLON");
            }
        };
        window.addEventListener("keydown", handleShortcut);
        return () => window.removeEventListener("keydown", handleShortcut);
    }, [activeDossier, saveDossier, saving]);

    const createDossier = async () => {
        if (!createEhpadId || !createDateProduction) {
            setError("Selectionnez un EHPAD et une date de production.");
            return;
        }
        setSaving(true);
        setError("");
        try {
            const created = (await createControleDossierLot(Number(createEhpadId), createDateProduction, DOSSIER_TYPE)).data;
            const initialData = defaultFormData(created);
            const saved = (await updateControleDossierLot(created.id, { status: "BROUILLON", donnees: initialData })).data;
            setSelectedEhpadId(String(saved.ehpadId));
            setActiveDossier(saved);
            setFormData(mergeFormData(saved.donnees, saved));
            await loadDossiers();
            setShowCreateModal(false);
        } catch (error) {
            setError(apiErrorMessage(error, "Creation du dossier impossible."));
        } finally {
            setSaving(false);
        }
    };

    const openDossier = (dossier) => {
        setActiveDossier(dossier);
        setSelectedEhpadId(String(dossier.ehpadId));
        setFormData(mergeFormData(dossier.donnees, dossier));
        setActiveTab("deconditionnement");
        setError("");
    };

    const uploadAttachments = async (files) => {
        const selectedFiles = Array.from(files || []);
        if (!activeDossier || selectedFiles.length === 0) return;
        setAttachmentUploading(true);
        setError("");
        try {
            let latestDossier = activeDossier;
            for (const file of selectedFiles) {
                latestDossier = (await uploadControleDossierLotAttachment(latestDossier.id, file)).data;
            }
            setActiveDossier(latestDossier);
            setFormData(mergeFormData(latestDossier.donnees, latestDossier));
            await loadDossiers();
        } catch (error) {
            setError(apiErrorMessage(error, "Import du fichier impossible."));
        } finally {
            setAttachmentUploading(false);
        }
    };

    const deleteAttachment = async (attachmentId) => {
        if (!activeDossier || !attachmentId) return;
        setAttachmentUploading(true);
        setError("");
        try {
            const response = await deleteControleDossierLotAttachment(activeDossier.id, attachmentId);
            setActiveDossier(response.data);
            setFormData(mergeFormData(response.data.donnees, response.data));
            await loadDossiers();
        } catch (error) {
            setError(apiErrorMessage(error, "Suppression du fichier impossible."));
        } finally {
            setAttachmentUploading(false);
        }
    };

    const renderCreateModal = () => showCreateModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.42)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 400, padding: 18 }}>
            <div style={{ width: "100%", maxWidth: 430, background: "white", borderRadius: 8, border: "1px solid #e2e8f0", boxShadow: "0 20px 45px rgba(15, 23, 42, 0.22)" }}>
                <div style={{ padding: "16px 18px", borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <h2 style={{ margin: 0, fontSize: 17, color: "#0f172a" }}>Creation dossier de lot</h2>
                    <button type="button" onClick={() => setShowCreateModal(false)} style={{ ...buttonBase, background: "white", color: "#475569" }}>Fermer</button>
                </div>
                <div style={{ padding: 18, display: "grid", gap: 12 }}>
                    <SearchableEhpadField ehpads={ehpads} value={createEhpadId} onChange={setCreateEhpadId} placeholder="Rechercher un EHPAD" />
                    <input style={inputStyle} type="date" value={createDateProduction} onChange={e => setCreateDateProduction(e.target.value)} />
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                        <button type="button" onClick={() => setShowCreateModal(false)} style={{ ...buttonBase, background: "white", color: "#334155" }}>Annuler</button>
                        <button type="button" onClick={createDossier} disabled={saving || !createEhpadId || !createDateProduction} style={{ ...buttonBase, background: "#2563eb", borderColor: "#2563eb", color: "white", fontWeight: 800 }}>Creer</button>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderDossierList = () => (
        <div style={{ display: "grid", gap: 12 }}>
            <div style={cardStyle}>
                <SearchableEhpadField ehpads={ehpads} value={selectedEhpadId} onChange={id => { setSelectedEhpadId(id); setActiveDossier(null); }} placeholder="Rechercher ou afficher tous" allowClear />
                <div style={{ color: "#64748b", fontSize: 13 }}>
                    {selectedEhpadId ? `${filteredDossiers.length} dossier(s) pour ${selectedEhpad?.nom || "cet EHPAD"}` : `${filteredDossiers.length} dossier(s) au total`}
                </div>
            </div>
            <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead><tr style={{ background: "#f8fafc" }}><th style={thStyle}>Date production</th><th style={thStyle}>EHPAD</th><th style={thStyle}>Reference lot</th><th style={thStyle}>Statut</th><th style={thStyle}>Derniere modification</th></tr></thead>
                    <tbody>
                        {filteredDossiers.map(dossier => (
                            <tr key={dossier.id} onClick={() => openDossier(dossier)} style={{ cursor: "pointer" }}>
                                <td style={tdStyle}>{formatDate(dossier.dateProduction)}</td>
                                <td style={tdStyle}>{dossier.ehpadNom}</td>
                                <td style={tdStyle}>{dossier.numeroLot}</td>
                                <td style={tdStyle}>{statusLabel(dossier.status)}</td>
                                <td style={tdStyle}>{dossier.updatedAt ? new Date(dossier.updatedAt).toLocaleString("fr-FR") : "-"}</td>
                            </tr>
                        ))}
                        {filteredDossiers.length === 0 && <tr><td colSpan={5} style={{ padding: 34, textAlign: "center", color: "#94a3b8" }}>Aucun dossier de lot de production.</td></tr>}
                    </tbody>
                </table>
            </div>
        </div>
    );

    const renderActiveDossier = () => {
        if (!activeDossier) return renderDossierList();
        return (
            <div style={{ display: "grid", gap: 12 }}>
                <div style={cardStyle}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                        <div>
                            <button type="button" onClick={() => setActiveDossier(null)} style={{ ...buttonBase, background: "white", color: "#0f172a", fontWeight: 800, marginBottom: 8 }}>Retour aux dossiers</button>
                            <h2 style={{ margin: 0, color: "#0f172a", fontSize: 18 }}>Fiche de tracabilite - Production PDA</h2>
                            <div style={{ color: "#64748b", fontSize: 13, marginTop: 3 }}>{activeDossier.ehpadNom} - Production du {formatDate(activeDossier.dateProduction)}</div>
                        </div>
                        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                            <button type="button" disabled={saving} onClick={() => saveDossier("BROUILLON")} style={{ ...buttonBase, background: "#0f172a", borderColor: "#0f172a", color: "white", fontWeight: 900 }}>Enregistrer</button>
                            <button type="button" disabled={saving} onClick={() => saveDossier("TERMINE")} style={{ ...buttonBase, background: "#16a34a", borderColor: "#15803d", color: "white", fontWeight: 900 }}>Valider</button>
                        </div>
                    </div>
                </div>

                <Section title="1. Identification">
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "end" }}>
                        <Field label="EHPAD" style={{ flex: "1 1 230px" }}><input style={inputStyle} value={formData.meta.ehpad || activeDossier.ehpadNom || ""} onChange={e => updateSection("meta", { ehpad: e.target.value })} /></Field>
                        <Field label="Date d'edition" style={{ flex: "0 1 180px" }}><input style={inputStyle} type="date" value={formData.meta.dateEdition || ""} onChange={e => updateSection("meta", { dateEdition: e.target.value })} /></Field>
                        <Field label="Reference du lot" style={{ flex: "2 1 390px" }}>
                            <div style={{ display: "grid", gridTemplateColumns: "minmax(130px, 1fr) auto minmax(130px, 1fr)", gap: 8, alignItems: "center", minWidth: 0 }}>
                                <input style={inputStyle} type="date" value={formData.meta.dateLotDebut || ""} onChange={e => updateSection("meta", { dateLotDebut: e.target.value })} />
                                <span style={{ color: "#475569", fontSize: 13, fontWeight: 900 }}>au</span>
                                <input style={inputStyle} type="date" value={formData.meta.dateLotFin || ""} onChange={e => updateSection("meta", { dateLotFin: e.target.value })} />
                            </div>
                        </Field>
                        <Field label="N° controle pharmacie" style={{ flex: "0 1 190px" }}><input style={inputStyle} value={formData.meta.numeroControlePharmacie || ""} onChange={e => updateSection("meta", { numeroControlePharmacie: e.target.value })} /></Field>
                    </div>
                </Section>

                <div role="tablist" aria-label="Fiches PDA" style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {DOSSIER_TABS.map(tab => (
                        <button
                            key={tab.key}
                            type="button"
                            role="tab"
                            aria-selected={activeTab === tab.key}
                            onClick={() => setActiveTab(tab.key)}
                            style={tabButtonStyle(activeTab === tab.key)}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                {activeTab === "deconditionnement" ? (
                    <DeconditionnementTab
                        data={formData.deconditionnement}
                        operatorLabel={operatorLabel}
                        onMetaChange={patch => updateSection("deconditionnement", patch)}
                        onRowsChange={rows => updateNestedRows("deconditionnement", "lignes", rows)}
                    />
                ) : (
                    <>
                <Section title="2. Production robot">
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 10 }}>
                        <Field label="Producteur / Robot"><input style={inputStyle} value={formData.productionRobot.producteurRobot || ""} onChange={e => updateSection("productionRobot", { producteurRobot: e.target.value })} /></Field>
                        <Field label="Residents actifs (P-Dose)"><input style={inputStyle} value={formData.productionRobot.residentsActifsPDose || ""} onChange={e => updateSection("productionRobot", { residentsActifsPDose: e.target.value })} /></Field>
                        <Field label="Residents produits (ecran robot)"><input style={inputStyle} value={formData.productionRobot.residentsProduitsRobot || ""} onChange={e => updateSection("productionRobot", { residentsProduitsRobot: e.target.value })} /></Field>
                        <Field label="Residents livres (D-Dose)"><input style={inputStyle} value={formData.productionRobot.residentsLivresDDose || formData.productionRobot.residentsLivresODose || ""} onChange={e => updateSection("productionRobot", { residentsLivresDDose: e.target.value })} /></Field>
                    </div>
                </Section>

                <Section title="3. Conditions de production">
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 10 }}>
                        <Field label="Heure de debut"><input style={inputStyle} type="time" value={formData.conditionsProduction.heureDebut || ""} onChange={e => updateSection("conditionsProduction", { heureDebut: e.target.value })} /></Field>
                        <Field label="Heure de fin"><input style={inputStyle} type="time" value={formData.conditionsProduction.heureFin || ""} onChange={e => updateSection("conditionsProduction", { heureFin: e.target.value })} /></Field>
                        <Field label="Temperature (15-25 °C)"><input style={inputStyle} value={formData.conditionsProduction.temperature || ""} onChange={e => updateSection("conditionsProduction", { temperature: e.target.value })} /></Field>
                        <Field label="Hygrometrie (< 60 %)"><input style={inputStyle} value={formData.conditionsProduction.hygrometrie || ""} onChange={e => updateSection("conditionsProduction", { hygrometrie: e.target.value })} /></Field>
                    </div>
                </Section>

                <EditableRowsSection title="4. Residents a produire / reproduire" rows={formData.residents} onChange={rows => updateRows("residents", rows)} columns={[
                    ["nomPrenom", "Nom et prenom", "textarea"],
                    ["motif", "Motif", "textarea"],
                    ["initiales", "Initiales (si reproduit)", "text"],
                ]} />

                <Section title="5. Incidents de production">
                    <div style={{ display: "grid", gap: 10 }}>
                        <div>
                            <div style={{ color: "#334155", fontSize: 12, fontWeight: 800, marginBottom: 6 }}>Problemes rencontres lors de la production ?</div>
                            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                <button type="button" onClick={() => updateSection("incidents", { problemes: "OUI" })} style={incidentButtonStyle(formData.incidents.problemes === "OUI", "#dc2626")}>OUI</button>
                                <button type="button" onClick={() => updateSection("incidents", { problemes: "NON" })} style={incidentButtonStyle(formData.incidents.problemes === "NON", "#16a34a")}>NON</button>
                            </div>
                        </div>
                        {formData.incidents.problemes === "OUI" && (
                            <Field label="Si oui, detailler">
                                <textarea style={{ ...inputStyle, minHeight: 80, resize: "vertical" }} value={formData.incidents.details || ""} onChange={e => updateSection("incidents", { details: e.target.value })} />
                            </Field>
                        )}
                    </div>
                </Section>

                <EditableRowsSection title="6. IPC - IN PROCESS CONTROL (1 controle tous les 10 rouleaux)" rows={formData.controlesQualite} onChange={rows => updateRows("controlesQualite", rows)} columns={[
                    ["patientControle", "Nom et prenom du patient controle", "textarea"],
                    ["ipcNumero", "IPC numero", "text"],
                ]} showIndex={false} footer={(
                    <Field label="Rouleaux patient mis a CYCLAMED">
                        <input style={inputStyle} value={formData.controlesQualiteMeta.rouleauxCyclamed || ""} onChange={e => updateSection("controlesQualiteMeta", { rouleauxCyclamed: e.target.value })} />
                    </Field>
                )} />

                <Section title="7. Contrôle POUCH INSPECTOR">
                    <ProductionAttachments dossierId={activeDossier.id} attachments={formData.piecesJointes || []} uploading={attachmentUploading} onUpload={uploadAttachments} onDelete={deleteAttachment} />
                </Section>

                <Section title="8. Reparation et controle final">
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 10 }}>
                        <Field label="Reparation effectuee par"><input style={inputStyle} value={formData.reparationControleFinal.realisePar || ""} onChange={e => updateSection("reparationControleFinal", { realisePar: e.target.value })} /></Field>
                        <Field label="Controle PI"><input style={inputStyle} value={formData.reparationControleFinal.controlePi || formData.reparationControleFinal.controlePar || ""} onChange={e => updateSection("reparationControleFinal", { controlePi: e.target.value })} /></Field>
                        <Field label="Pontage Rouleaux + edition bon de livraison"><input style={inputStyle} value={formData.reparationControleFinal.pontageRouleaux || formData.reparationControleFinal.pointageMachine || ""} onChange={e => updateSection("reparationControleFinal", { pontageRouleaux: e.target.value })} /></Field>
                        <Field label="Date et heure"><input style={inputStyle} type="datetime-local" value={formData.reparationControleFinal.dateHeure || ""} onChange={e => updateSection("reparationControleFinal", { dateHeure: e.target.value })} /></Field>
                    </div>
                </Section>

                <Section title="9. Libération de lot">
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 10 }}>
                        <Field label="Nom du pharmacien"><input style={inputStyle} value={formData.liberationLot.pharmacien || ""} onChange={e => updateSection("liberationLot", { pharmacien: e.target.value })} /></Field>
                        <Field label="Date"><input style={inputStyle} type="date" value={formData.liberationLot.date || ""} onChange={e => updateSection("liberationLot", { date: e.target.value })} /></Field>
                        <Field label="Heure"><input style={inputStyle} type="time" value={formData.liberationLot.heure || ""} onChange={e => updateSection("liberationLot", { heure: e.target.value })} /></Field>
                        <Field label="Signature et cachet"><input style={inputStyle} value={formData.liberationLot.signatureCachet || ""} onChange={e => updateSection("liberationLot", { signatureCachet: e.target.value })} /></Field>
                    </div>
                </Section>
                    </>
                )}
            </div>
        );
    };

    if (loading) return <div style={{ padding: 28, color: "#64748b" }}>Chargement du dossier de lot...</div>;

    return (
        <div style={{ padding: 10, background: "#f8fafc", minHeight: "100%" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 10, flexWrap: "wrap" }}>
                <div>
                    <h1 style={{ margin: 0, fontSize: activeDossier ? 18 : 22, color: "#0f172a" }}>{service?.name || "Dossier de lot de production"}</h1>
                    {!activeDossier && <div style={{ color: "#64748b", fontSize: 13, marginTop: 3 }}>Fiches de tracabilite production PDA par EHPAD.</div>}
                </div>
                <button type="button" onClick={() => { setCreateEhpadId(selectedEhpadId || ""); setCreateDateProduction(todayISO()); setShowCreateModal(true); setError(""); }} style={{ ...buttonBase, background: "#2563eb", borderColor: "#2563eb", color: "white", fontWeight: 800 }}>
                    Creation dossier de lot
                </button>
            </div>
            {error && <div style={{ marginBottom: 12, padding: "10px 12px", background: "#fef2f2", color: "#991b1b", border: "1px solid #fecaca", borderRadius: 8, fontSize: 13 }}>{error}</div>}
            {renderActiveDossier()}
            {renderCreateModal()}
        </div>
    );
}

function Section({ title, children }) {
    return (
        <section style={sectionStyle}>
            <h2 style={{ margin: 0, color: "#0f172a", fontSize: 16, fontWeight: 950 }}>{title}</h2>
            {children}
        </section>
    );
}

function Field({ label, children, style = {} }) {
    return <label style={{ ...labelStyle, minWidth: 0, ...style }}><span>{label}</span>{children}</label>;
}

function EditableRowsSection({ title, rows, onChange, columns, showNumber = false, showIndex = true, footer = null }) {
    const safeRows = Array.isArray(rows) ? rows : [];
    const updateRow = (index, key, value) => onChange(safeRows.map((row, rowIndex) => rowIndex === index ? { ...row, [key]: value } : row));
    const addRow = () => onChange([...safeRows, { id: `row-${Date.now()}` }]);
    const removeRow = index => onChange(safeRows.filter((_, rowIndex) => rowIndex !== index));

    return (
        <Section title={title}>
            <div style={{ overflowX: "auto", border: "1px solid #cbd5e1", borderRadius: 8 }}>
                <table style={{ width: "100%", minWidth: 820, borderCollapse: "collapse", background: "white" }}>
                    <thead>
                        <tr>
                            {showIndex && <th style={{ ...headerCellStyle, width: 58 }}>N</th>}
                            {columns.map(([key, label]) => <th key={key} style={headerCellStyle}>{label}</th>)}
                            <th style={{ ...headerCellStyle, width: 54 }} />
                        </tr>
                    </thead>
                    <tbody>
                        {safeRows.length === 0 && (
                            <tr>
                                <td colSpan={columns.length + (showIndex ? 2 : 1)} style={{ ...bodyCellStyle, padding: 18, textAlign: "center", color: "#94a3b8", fontSize: 13 }}>
                                    Aucune ligne ajoutee.
                                </td>
                            </tr>
                        )}
                        {safeRows.map((row, index) => (
                            <tr key={row.id || index}>
                                {showIndex && <td style={{ ...bodyCellStyle, textAlign: "center", fontWeight: 900 }}>{showNumber ? (row.numero || index + 1) : index + 1}</td>}
                                {columns.map(([key, label, type]) => (
                                    <td key={key} style={bodyCellStyle}>
                                        {type === "textarea" ? (
                                            <textarea style={{ ...inputStyle, minHeight: 52, resize: "vertical" }} value={row[key] || ""} onChange={e => updateRow(index, key, e.target.value)} placeholder={label} />
                                        ) : (
                                            <input style={inputStyle} value={row[key] || ""} onChange={e => updateRow(index, key, e.target.value)} placeholder={label} />
                                        )}
                                    </td>
                                ))}
                                <td style={{ ...bodyCellStyle, textAlign: "center" }}>
                                    <button type="button" onClick={() => removeRow(index)} style={{ ...buttonBase, padding: "5px 9px", background: "white", color: "#dc2626" }}>x</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <button type="button" onClick={addRow} style={{ ...buttonBase, background: "#0f172a", borderColor: "#0f172a", color: "white", fontWeight: 900 }}>Ajouter une ligne</button>
            </div>
            {footer}
        </Section>
    );
}

function DeconditionnementTab({ data, operatorLabel, onMetaChange, onRowsChange }) {
    const rows = Array.isArray(data?.lignes) ? data.lignes : [];
    const updateRow = (index, key, value) => onRowsChange(rows.map((row, rowIndex) => rowIndex === index ? { ...row, [key]: value } : row));
    const addRow = () => onRowsChange([...rows, { id: `deconditionnement-${Date.now()}` }]);
    const removeRow = index => onRowsChange(rows.filter((_, rowIndex) => rowIndex !== index));
    const toggleTrace = (index, prefix, checked) => {
        const stamp = checked ? new Date().toISOString().slice(0, 16) : "";
        const tracedBy = checked ? operatorLabel : "";
        onRowsChange(rows.map((row, rowIndex) => rowIndex === index ? {
            ...row,
            [`${prefix}Checked`]: checked,
            [`${prefix}DateHeure`]: stamp,
            [`${prefix}Par`]: tracedBy,
        } : row));
    };

    return (
        <div style={{ display: "grid", gap: 12 }}>
            <Section title="2. Déconditionnement - déblistérage">
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 10 }}>
                    <Field label="Campagne n°"><input style={inputStyle} value={data?.campagneNumero || ""} onChange={e => onMetaChange({ campagneNumero: e.target.value })} /></Field>
                    <Field label="Date"><input style={inputStyle} type="date" value={data?.date || ""} onChange={e => onMetaChange({ date: e.target.value })} /></Field>
                    <Field label="Ligne / poste"><input style={inputStyle} value={data?.lignePoste || ""} onChange={e => onMetaChange({ lignePoste: e.target.value })} /></Field>
                    <Field label="Pharmacien responsable"><input style={inputStyle} value={data?.pharmacienResponsable || ""} onChange={e => onMetaChange({ pharmacienResponsable: e.target.value })} /></Field>
                </div>
            </Section>

            <Section title="Tableau de déconditionnement">
                <div style={{ overflowX: "auto", border: "1px solid #cbd5e1", borderRadius: 8 }}>
                    <table style={{ width: "100%", minWidth: 1280, borderCollapse: "collapse", background: "white" }}>
                        <thead>
                            <tr>
                                <th style={{ ...headerCellStyle, width: 46 }}>N</th>
                                <th style={headerCellStyle}>Spécialité · dosage</th>
                                <th style={headerCellStyle}>Lot fabricant</th>
                                <th style={headerCellStyle}>Péremption fabricant</th>
                                <th style={headerCellStyle}>Qté déblist.</th>
                                <th style={headerCellStyle}>N° boîte interm.</th>
                                <th style={headerCellStyle}>Déconditionné</th>
                                <th style={headerCellStyle}>Contrôle tiers</th>
                                <th style={headerCellStyle}>Vide de ligne P-07</th>
                                <th style={headerCellStyle}>Observations · écart</th>
                                <th style={{ ...headerCellStyle, width: 54 }} />
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length === 0 && (
                                <tr>
                                    <td colSpan={11} style={{ ...bodyCellStyle, padding: 18, textAlign: "center", color: "#94a3b8", fontSize: 13 }}>
                                        Aucune ligne ajoutee.
                                    </td>
                                </tr>
                            )}
                            {rows.map((row, index) => (
                                <tr key={row.id || index}>
                                    <td style={{ ...bodyCellStyle, textAlign: "center", fontWeight: 900 }}>{index + 1}</td>
                                    <td style={bodyCellStyle}><textarea style={{ ...inputStyle, minHeight: 52, resize: "vertical" }} value={row.specialiteDosage || ""} onChange={e => updateRow(index, "specialiteDosage", e.target.value)} placeholder="Spécialité · dosage" /></td>
                                    <td style={bodyCellStyle}><input style={inputStyle} value={row.lotFabricant || ""} onChange={e => updateRow(index, "lotFabricant", e.target.value)} placeholder="Lot" /></td>
                                    <td style={bodyCellStyle}><input style={inputStyle} type="date" value={row.peremptionFabricant || ""} onChange={e => updateRow(index, "peremptionFabricant", e.target.value)} /></td>
                                    <td style={bodyCellStyle}><input style={inputStyle} value={row.quantiteDeblisterage || ""} onChange={e => updateRow(index, "quantiteDeblisterage", e.target.value)} placeholder="Qté" /></td>
                                    <td style={bodyCellStyle}><input style={inputStyle} value={row.numeroBoiteIntermediaire || ""} onChange={e => updateRow(index, "numeroBoiteIntermediaire", e.target.value)} placeholder="Boîte" /></td>
                                    <td style={bodyCellStyle}><TraceCheckbox label="Déconditionné" checked={!!row.deconditionneChecked} dateHeure={row.deconditionneDateHeure} operator={row.deconditionnePar} onToggle={checked => toggleTrace(index, "deconditionne", checked)} /></td>
                                    <td style={bodyCellStyle}><TraceCheckbox label="Contrôlé" checked={!!row.controleChecked} dateHeure={row.controleDateHeure} operator={row.controlePar} onToggle={checked => toggleTrace(index, "controle", checked)} /></td>
                                    <td style={bodyCellStyle}>
                                        <label style={checkboxLabelStyle}>
                                            <input type="checkbox" checked={!!row.videLigneP07} onChange={e => updateRow(index, "videLigneP07", e.target.checked)} />
                                            <span>Validé</span>
                                        </label>
                                    </td>
                                    <td style={bodyCellStyle}><textarea style={{ ...inputStyle, minHeight: 52, resize: "vertical" }} value={row.observationsEcart || ""} onChange={e => updateRow(index, "observationsEcart", e.target.value)} placeholder="Observations · écart" /></td>
                                    <td style={{ ...bodyCellStyle, textAlign: "center" }}>
                                        <button type="button" onClick={() => removeRow(index)} style={{ ...buttonBase, padding: "5px 9px", background: "white", color: "#dc2626" }}>x</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <button type="button" onClick={addRow} style={{ ...buttonBase, background: "#0f172a", borderColor: "#0f172a", color: "white", fontWeight: 900 }}>Ajouter une ligne</button>
                </div>
            </Section>
        </div>
    );
}

function TraceCheckbox({ label, checked, dateHeure, operator, onToggle }) {
    return (
        <div style={{ display: "grid", gap: 6 }}>
            <label style={checkboxLabelStyle}>
                <input type="checkbox" checked={checked} onChange={e => onToggle(e.target.checked)} />
                <span>{label}</span>
            </label>
            {(dateHeure || operator) && (
                <div style={{ color: "#475569", fontSize: 12, lineHeight: 1.35 }}>
                    {dateHeure ? new Date(dateHeure).toLocaleString("fr-FR") : "-"}
                    {operator && <><br />{operator}</>}
                </div>
            )}
        </div>
    );
}

function tabButtonStyle(active) {
    return {
        ...buttonBase,
        background: active ? "#0f172a" : "white",
        borderColor: active ? "#0f172a" : "#cbd5e1",
        color: active ? "white" : "#334155",
        fontWeight: active ? 900 : 700,
    };
}

function incidentButtonStyle(active, color) {
    return {
        ...buttonBase,
        background: active ? color : "white",
        borderColor: active ? color : "#cbd5e1",
        color: active ? "white" : "#0f172a",
        fontWeight: 900,
        minWidth: 84,
    };
}

const checkboxLabelStyle = {
    display: "flex",
    gap: 8,
    alignItems: "center",
    color: "#0f172a",
    fontSize: 13,
    fontWeight: 800,
};

function SearchableEhpadField({ ehpads, value, onChange, placeholder, allowClear = false }) {
    const selected = ehpads.find(e => String(e.id) === String(value));
    const [query, setQuery] = useState(selected?.nom || "");
    const [open, setOpen] = useState(false);

    useEffect(() => setQuery(selected?.nom || ""), [selected?.id, selected?.nom]);

    const matches = useMemo(() => {
        const normalizedQuery = String(query || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
        if (!normalizedQuery) return ehpads.slice(0, 60);
        return ehpads.filter(ehpad => String(`${ehpad.nom} ${ehpad.code || ""} ${ehpad.tournee || ""}`).normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().includes(normalizedQuery)).slice(0, 60);
    }, [ehpads, query]);

    const clear = () => {
        onChange("");
        setQuery("");
        setOpen(false);
    };

    return (
        <div style={{ position: "relative" }}>
            <div style={{ display: "flex", gap: 6 }}>
                <input style={inputStyle} value={query} placeholder={placeholder} onChange={event => { setQuery(event.target.value); setOpen(true); if (value) onChange(""); }} onFocus={() => setOpen(true)} autoComplete="off" />
                {allowClear && value && <button type="button" onClick={clear} style={{ ...buttonBase, background: "white", color: "#475569" }}>Tous</button>}
            </div>
            {open && (
                <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, maxHeight: 320, overflowY: "auto", background: "white", border: "1px solid #cbd5e1", borderRadius: 7, boxShadow: "0 10px 24px rgba(15, 23, 42, 0.12)", zIndex: 20 }}>
                    {allowClear && <button type="button" onMouseDown={e => e.preventDefault()} onClick={clear} style={ehpadOptionStyle}>Tous les EHPAD</button>}
                    {matches.map(ehpad => (
                        <button key={ehpad.id} type="button" onMouseDown={e => e.preventDefault()} onClick={() => { onChange(String(ehpad.id)); setQuery(ehpad.nom); setOpen(false); }} style={{ ...ehpadOptionStyle, background: String(ehpad.id) === String(value) ? "#eff6ff" : "white" }}>
                            <span style={{ fontWeight: 800 }}>{ehpad.nom}</span>{ehpad.tournee && <span style={{ color: "#64748b" }}> - {ehpad.tournee}</span>}
                        </button>
                    ))}
                    {matches.length === 0 && <div style={{ padding: "10px 12px", color: "#94a3b8", fontSize: 13 }}>Aucun EHPAD trouve.</div>}
                </div>
            )}
        </div>
    );
}

function ProductionAttachments({ dossierId, attachments, uploading, onUpload, onDelete }) {
    return (
        <div style={{ display: "grid", gap: 12 }}>
            <label style={{ ...buttonBase, display: "inline-flex", justifySelf: "start", background: "#2563eb", borderColor: "#2563eb", color: "white", fontWeight: 900, cursor: uploading ? "not-allowed" : "pointer", opacity: uploading ? 0.65 : 1 }}>
                {uploading ? "Import..." : "Importer PDF / image"}
                <input type="file" accept="application/pdf,image/png,image/jpeg" multiple disabled={uploading} onChange={event => { onUpload(event.target.files); event.target.value = ""; }} style={{ display: "none" }} />
            </label>
            {attachments.length === 0 ? (
                <div style={{ padding: 18, borderRadius: 8, border: "1px dashed #cbd5e1", background: "#f8fafc", color: "#94a3b8", fontSize: 13, textAlign: "center" }}>Aucun document importe.</div>
            ) : (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 12 }}>
                    {attachments.map(attachment => <AttachmentPreview key={attachment.id} dossierId={dossierId} attachment={attachment} disabled={uploading} onDelete={onDelete} />)}
                </div>
            )}
        </div>
    );
}

function AttachmentPreview({ dossierId, attachment, disabled, onDelete }) {
    const [objectUrl, setObjectUrl] = useState("");
    const [loadError, setLoadError] = useState("");
    const isPdf = String(attachment.contentType || "").includes("pdf") || String(attachment.fileName || "").toLowerCase().endsWith(".pdf");
    const isImage = String(attachment.contentType || "").startsWith("image/");

    useEffect(() => {
        let cancelled = false;
        let localUrl = "";
        setLoadError("");
        setObjectUrl("");
        if (!dossierId || !attachment?.id) return undefined;
        getControleDossierLotAttachmentContent(dossierId, attachment.id)
            .then(response => {
                if (cancelled) return;
                localUrl = URL.createObjectURL(response.data);
                setObjectUrl(localUrl);
            })
            .catch(() => { if (!cancelled) setLoadError("Apercu indisponible."); });
        return () => {
            cancelled = true;
            if (localUrl) URL.revokeObjectURL(localUrl);
        };
    }, [dossierId, attachment?.id]);

    return (
        <div style={{ display: "grid", gap: 8, padding: 10, borderRadius: 8, border: "1px solid #e2e8f0", background: "#f8fafc" }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
                {objectUrl ? (
                    <a
                        href={objectUrl}
                        target="_blank"
                        rel="noreferrer"
                        title="Ouvrir le document dans un nouvel onglet"
                        style={{ minWidth: 0, color: "#2563eb", fontSize: 13, fontWeight: 900, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                    >
                        {attachment.fileName || "Document"}
                    </a>
                ) : (
                    <div style={{ minWidth: 0, color: "#0f172a", fontSize: 13, fontWeight: 900, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{attachment.fileName || "Document"}</div>
                )}
                <button type="button" disabled={disabled} onClick={() => { if (window.confirm("Supprimer ce document importe ?")) onDelete(attachment.id); }} style={{ ...buttonBase, padding: "5px 9px", background: "white", color: "#dc2626", opacity: disabled ? 0.55 : 1 }}>x</button>
            </div>
            <div style={{ height: 360, borderRadius: 8, border: "1px solid #e2e8f0", background: "white", overflow: "hidden", display: "grid", placeItems: "center" }}>
                {loadError ? <div style={{ color: "#94a3b8", fontSize: 13 }}>{loadError}</div> : !objectUrl ? <div style={{ color: "#94a3b8", fontSize: 13 }}>Chargement...</div> : isPdf ? <iframe title={attachment.fileName || attachment.id} src={objectUrl} style={{ width: "100%", height: "100%", border: 0 }} /> : isImage ? <img src={objectUrl} alt={attachment.fileName || "Document importe"} style={{ width: "100%", height: "100%", objectFit: "contain" }} /> : <a href={objectUrl} target="_blank" rel="noreferrer" style={{ color: "#2563eb", fontWeight: 800 }}>Ouvrir le document</a>}
            </div>
        </div>
    );
}

const ehpadOptionStyle = { display: "block", width: "100%", padding: "9px 11px", border: 0, borderBottom: "1px solid #f1f5f9", background: "white", color: "#0f172a", textAlign: "left", cursor: "pointer", fontSize: 13 };
